import { useCallback, useRef, useState } from "react";
import { ApiError, fieldMessage, formMessage } from "../Services/http";

/**
 * Formulärinlämning med skydd mot dubbel-submit och fel per fält.
 *
 * DUBBEL-SUBMIT: en disabled knapp räcker inte. Enter i ett fält skickar
 * formuläret, en dubbelklickning hinner före omritningen, och React
 * batchar state-uppdateringar — så pending som state kan hinna vara false
 * vid det andra anropet. Ref:en nedan sätts synkront och är den som
 * faktiskt håller emot; pending-state finns för att rita knappen.
 */
export type SubmitResult<T> =
  | { ok: true; data: T }
  /** blocked: ett anrop pågick redan och det här trycket ignorerades. */
  | { ok: false; blocked?: true };

export function useSubmit<T>(action: (...args: any[]) => Promise<T>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const inFlight = useRef(false);

  /**
   * Returnerar ett resultatobjekt och inte värdet direkt.
   *
   * En action som inte returnerar något — login(), till exempel — ger
   * undefined vid framgång, vilket är exakt vad ett fångat fel också
   * skulle ge. Anroparen kunde då inte skilja "det gick bra" från "det
   * gick fel", och navigerade antingen alltid eller aldrig vidare.
   */
  const submit = useCallback(
    async (...args: any[]): Promise<SubmitResult<T>> => {
      if (inFlight.current) return { ok: false, blocked: true };

      inFlight.current = true;
      setPending(true);
      setError(null);

      try {
        const data = await action(...args);
        return { ok: true, data };
      } catch (e) {
        setError(e);
        return { ok: false };
      } finally {
        inFlight.current = false;
        setPending(false);
      }
    },
    [action]
  );

  return {
    submit,
    pending,
    error,

    /** Felet för ett visst fält, redan översatt till läsbar text. */
    fieldError: (name: string) => fieldMessage(error, name),

    /** Formulärets sammanfattande fel, när servern inte pekat ut fält. */
    formError: formMessage(error),

    /** Rensar felet, t.ex. när användaren börjar skriva om. */
    clearError: () => setError(null),

    isApiError: error instanceof ApiError,
  };
}
